import { ANNOUNCEMENTS } from './announcements.ts';
import type { Announcement, AnnouncementFlagKey, AnnouncementType } from './types.ts';

/**
 * Canonical display label per entry type, shared by every announcement
 * surface (in-app badge, marketing digest pill) so the two never diverge.
 */
export const ANNOUNCEMENT_TYPE_LABELS: Record<AnnouncementType, string> = {
  new: 'New',
  improved: 'Improved',
  'under-the-hood': 'Under the hood',
};

/** Descending string compare; 0 on ties keeps the catalog's editorial order
 *  (flagship entries are authored first within a release). */
const byDescending = (a: string, b: string): number =>
  b < a ? -1 : a < b ? 1 : 0;

/**
 * In-app visibility: flag-gated entries follow the viewer's resolved flag;
 * ungated entries show once they're publicly visible at all. `status` is
 * otherwise a public/digest concern only — a flagged entry with
 * `status: 'hidden'` still renders in-app for workspaces that have the flag
 * (that's the early-access experience).
 */
export function isVisibleInApp(
  entry: Announcement,
  hasFlag: (key: AnnouncementFlagKey) => boolean,
): boolean {
  return entry.flagKey ? hasFlag(entry.flagKey) : entry.status !== 'hidden';
}

/**
 * A flag-gated entry that hasn't reached GA reads as early access — a viewer
 * seeing it has the feature ahead of general release. Shared by every surface
 * that frames entries this way so the predicate never diverges.
 */
export function isEarlyAccess(entry: Announcement): boolean {
  return Boolean(entry.flagKey) && entry.status !== 'ga';
}

/** In-app feed shows entries no older than this, keeping the dialog short. */
export const FEED_WINDOW_DAYS = 90;
/** Hard cap after the window — a busy quarter can't produce a 20-card scroll. */
export const FEED_MAX_ENTRIES = 8;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The date an entry is "new" from: GA restarts the clock (a slow-rollout
 * feature becomes broadly new when it reaches everyone). This is the single
 * recency key — the feed sorts/windows by it, the card shows it, and it's the
 * discovery `releasedAt` stamp — so a recently-GA'd, long-published entry
 * can't sort as new yet display a stale date or fail to count as unseen.
 */
export function announcementDate(entry: Announcement): string {
  return entry.gaDate ?? entry.publishedAt;
}

/**
 * The in-app What's-New feed for a viewer: visible entries (see
 * {@link isVisibleInApp}) within the last {@link FEED_WINDOW_DAYS} days,
 * newest first, capped at {@link FEED_MAX_ENTRIES}. Aging out is automatic —
 * the catalog stays append-only (the digest needs full history) and nobody
 * retires entries by hand. Older entries live on the marketing changelog.
 */
export function feedEntries(
  hasFlag: (key: AnnouncementFlagKey) => boolean,
  now: Date,
  entries: Announcement[] = ANNOUNCEMENTS,
): Announcement[] {
  const cutoff = now.getTime() - FEED_WINDOW_DAYS * DAY_MS;
  return entries
    .filter(
      (entry) =>
        isVisibleInApp(entry, hasFlag) &&
        new Date(announcementDate(entry)).getTime() >= cutoff,
    )
    .sort((a, b) => byDescending(announcementDate(a), announcementDate(b)))
    .slice(0, FEED_MAX_ENTRIES);
}

export interface DigestMonth {
  /** `YYYY-MM` */
  month: string;
  entries: Announcement[];
}

/**
 * The anonymous marketing digest: GA entries grouped by `gaDate` month,
 * newest month first. No viewer context, so gating is purely the entry's
 * `status` — and only `ga` entries render publicly (early-access features
 * stay off the public page until they reach everyone). GA entries are dated
 * by the month the feature became real for everyone (`gaDate`), not the
 * month the code shipped.
 */
export function digestEntries(
  entries: Announcement[] = ANNOUNCEMENTS,
): DigestMonth[] {
  const byMonth = new Map<string, Announcement[]>();
  for (const entry of entries) {
    if (entry.status !== 'ga' || !entry.gaDate) continue;
    const month = entry.gaDate.slice(0, 7);
    const bucket = byMonth.get(month);
    if (bucket) bucket.push(entry);
    else byMonth.set(month, [entry]);
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => byDescending(a, b))
    .map(([month, monthEntries]) => ({
      month,
      entries: [...monthEntries].sort((a, b) =>
        byDescending(a.gaDate ?? '', b.gaDate ?? ''),
      ),
    }));
}

/** `"2026-07"` → `"July 2026"` (UTC-stable, no locale drift in builds). */
export function monthLabel(month: string): string {
  const [year, monthIndex] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(monthIndex) - 1, 1));
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
